import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, runTransaction,
  writeBatch, updateDoc, arrayRemove, arrayUnion,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Send , ChevronLeft } from 'lucide-react';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

/**
 * My Messages, student and guardian view.
 *
 * A separate module from the staff My Message, not a role branch inside it. Families can only ever
 * read and reply: there is no compose, no recipient picker, no message groups. Those controls are
 * the entire top half of the staff module, so sharing one component would mean guarding each of
 * them by role -- which is the arrangement every role-race bug in this project came from.
 *
 * Nothing here can start a conversation. A thread appears because a member of staff addressed it to
 * this account, and `participants` is fixed at creation: the security rule refuses any update that
 * changes it, so replying cannot pull anyone else in.
 */
const messageDocId = (threadId, subId) => `${threadId}__${String(subId).padStart(4, '0')}`;

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : `${d.toLocaleDateString('en-CA')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const MyMessagesUser = () => {
  const { activeSchoolId, isMobile, userProfile } = useOutletContext();
  const { t } = useT();

  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownId = userProfile?.studentId || userProfile?.initial || ownEmail;
  const ownName = userProfile?.name || ownId;

  const [threads, setThreads] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [messages, setMessages] = useState([]);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const selectedThread = useMemo(
    () => threads.find(item => item.uid === selectedThreadId) || null,
    [threads, selectedThreadId],
  );

  // Scoped by participants, which is the field the rule tests. Anything else would be refused
  // outright rather than returning fewer rows.
  useEffect(() => {
    if (!activeSchoolId || !ownEmail) return;
    const q = query(
      collection(db, 'schools', activeSchoolId, 'message_thread'),
      where('participants', 'array-contains', ownEmail),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => String(b.lastAt || '').localeCompare(String(a.lastAt || '')));
      setThreads(list);
      setLoadingThreads(false);
    }, (e) => { console.error('Error loading threads', e); setLoadingThreads(false); });
    return () => unsub();
  }, [activeSchoolId, ownEmail]);

  useEffect(() => {
    if (!activeSchoolId || !selectedThreadId) { setMessages([]); return; }
    // No orderBy: paired with the equality filter it would need a composite index deployed before
    // the module worked at all. Threads are short, so sort client-side.
    const q = query(
      collection(db, 'schools', activeSchoolId, 'message_record'),
      where('message_id', '==', selectedThreadId),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ uid: d.id, ...d.data() }));
      list.sort((a, b) => (a.sub_id || 0) - (b.sub_id || 0));
      setMessages(list);
    }, (e) => console.error('Error loading messages', e));
    return () => unsub();
  }, [activeSchoolId, selectedThreadId]);

  // Mark as read on open.
  useEffect(() => {
    if (!selectedThread || !(selectedThread.unreadFor || []).includes(ownEmail)) return;
    const run = async () => {
      try {
        await updateDoc(doc(db, 'schools', activeSchoolId, 'message_thread', selectedThread.uid), {
          unreadFor: arrayRemove(ownEmail),
        });
        const unseen = messages.filter(m => m.senderEmail !== ownEmail && !(m.readBy || []).includes(ownEmail));
        if (unseen.length) {
          const batch = writeBatch(db);
          unseen.forEach(m =>
            batch.update(doc(db, 'schools', activeSchoolId, 'message_record', m.uid), { readBy: arrayUnion(ownEmail) }));
          await batch.commit();
        }
      } catch (e) {
        console.error('Could not mark as read', e);
      }
    };
    run();
  }, [selectedThread, messages, activeSchoolId, ownEmail]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !selectedThread) return;
    setSending(true);
    try {
      const threadRef = doc(db, 'schools', activeSchoolId, 'message_thread', selectedThread.uid);
      const now = new Date().toISOString();
      const body = replyBody.trim();

      // sub_id is allocated inside a transaction: computing "highest + 1" outside one means two
      // simultaneous replies write the same document id and a message is silently lost.
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(threadRef);
        if (!snap.exists()) throw new Error(t('myMessages.gone'));
        const data = snap.data();
        const nextSubId = (data.lastSubId || 0) + 1;
        const others = (data.participants || []).filter(p => p !== ownEmail);

        tx.set(doc(db, 'schools', activeSchoolId, 'message_record', messageDocId(selectedThread.uid, nextSubId)), {
          message_id: selectedThread.uid,
          sub_id: nextSubId,
          subject: data.subject || '',
          // Copied from the thread, never composed here: the rule requires the sender to be a
          // participant, and a reply must not be able to widen who is on the conversation.
          participants: data.participants,
          senderEmail: ownEmail, senderId: ownId, senderName: ownName,
          receiverEmail: data.isGroup ? '' : (others[0] || ''),
          receiverId: '', receiverName: '',
          content: body,
          datetime: now,
          readBy: [],
        });

        tx.update(threadRef, {
          lastSubId: nextSubId, lastAt: now, lastSnippet: body.slice(0, 120),
          lastSenderEmail: ownEmail, lastSenderId: ownId,
          unreadFor: others,
        });
      });

      setReplyBody('');
    } catch (err) {
      console.error('Error replying', err);
      alert(t('myMessages.sendFailed', { message: err.message }));
    }
    setSending(false);
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  };

  // On mobile the list and the conversation are separate screens: showing both at 375px leaves
  // neither usable.
  const showList = !isMobile || !selectedThreadId;
  const showThread = !isMobile || !!selectedThreadId;

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* The screen used to open with a tabs bar whose only tab was a label with no onClick --
          the tab bar in the shell now names the screen, so all that is left here is the control
          that genuinely belongs to it. The row disappears entirely when there is nothing in it,
          which for a student is always. */}
      {isMobile && selectedThreadId && (
        <div className="theme-tabs-bar">
          <button className="btn-outline" onClick={() => setSelectedThreadId('')} style={{ width: 'auto', padding: '0.55rem 0.9rem' }}>
            <ChevronLeft size={16} /> {t('myMessages.allMessages')}
          </button>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: isMobile ? 0 : '1rem', padding: isMobile ? 0 : '0 1.5rem 1.5rem' }}>
        {showList && (
          <div style={{ ...card, width: isMobile ? '100%' : '320px', flexShrink: 0, overflowY: 'auto' }}>
            {loadingThreads ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
            ) : threads.length === 0 ? (
              <div style={{ padding: '2.5rem 1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                {t('myMessages.none')}
              </div>
            ) : threads.map(thread => {
              const unread = (thread.unreadFor || []).includes(ownEmail);
              const active = thread.uid === selectedThreadId;
              return (
                <button
                  key={thread.uid}
                  onClick={() => setSelectedThreadId(thread.uid)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                    border: 'none', borderBottom: '1px solid var(--color-border)',
                    background: active ? 'var(--color-primary-light)' : 'transparent',
                    padding: '0.85rem 1rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'baseline' }}>
                    <strong style={{ fontSize: '0.9rem', fontWeight: unread ? '800' : '600' }}>
                      {thread.subject || '(no subject)'}
                    </strong>
                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                      {formatWhen(thread.lastAt)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {thread.lastSnippet || ''}
                  </div>
                  {unread && (
                    <span style={{ display: 'inline-block', marginTop: '0.35rem', fontSize: '0.68rem', fontWeight: '700', color: 'var(--color-primary)' }}>
                      {t('myMessages.unread')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {showThread && (
          <div style={{ ...card, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {!selectedThread ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)' }}>
                {t('myMessages.selectOne')}
              </div>
            ) : (
              <>
                <div style={{ padding: '0.9rem 1.1rem', borderBottom: '1px solid var(--color-border)' }}>
                  <strong style={{ fontSize: '0.95rem' }}>{selectedThread.subject || '(no subject)'}</strong>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {messages.map(m => {
                    const mine = m.senderEmail === ownEmail;
                    return (
                      <div key={m.uid} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
                        {!mine && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: '0.15rem' }}>
                            {m.senderName || m.senderId || m.senderEmail}
                          </div>
                        )}
                        <div style={{
                          backgroundColor: mine ? 'var(--color-primary)' : 'var(--color-bg-app)',
                          color: mine ? '#fff' : 'var(--color-text-main)',
                          borderRadius: 'var(--radius-md)',
                          padding: '0.55rem 0.8rem',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>
                          {m.content}
                        </div>
                        <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', marginTop: '0.15rem', textAlign: mine ? 'right' : 'left' }}>
                          {formatWhen(m.datetime)}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                <form onSubmit={handleReply} style={{ display: 'flex', gap: '0.5rem', padding: '0.75rem', borderTop: '1px solid var(--color-border)' }}>
                  <input
                    type="text"
                    className="input-field"
                    style={{ flex: 1, margin: 0 }}
                    placeholder={t('myMessages.reply')}
                    value={replyBody}
                    onChange={e => setReplyBody(e.target.value)}
                    disabled={sending}
                  />
                  <button type="submit" className="btn-primary" disabled={sending || !replyBody.trim()} style={{ width: 'auto', padding: '0.55rem 1rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Send size={15} /> {sending ? t('myMessages.sending') : t('myMessages.send')}
                  </button>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyMessagesUser;
